import { useHead } from '@vueuse/head'

export interface SEOConfig {
  title: string
  description: string
  keywords?: string
  image?: string
  url?: string
  type?: 'website' | 'article' | 'property'
  structuredData?: any
}

export interface PropertyStructuredData {
  '@context': string
  '@type': string
  name: string
  description: string
  image: string[]
  address: {
    '@type': string
    addressLocality: string
    addressCountry: string
  }
  offers: {
    '@type': string
    price: string
    priceCurrency: string
    availability: string
  }
  numberOfRooms?: number
  floorSize?: {
    '@type': string
    value: number
    unitCode: string
  }
}

export interface BreadcrumbStructuredData {
  '@context': string
  '@type': string
  itemListElement: Array<{
    '@type': string
    position: number
    name: string
    item?: string
  }>
}

export class SEOService {
  private static instance: SEOService

  public static getInstance (): SEOService {
    if (!SEOService.instance) {
      SEOService.instance = new SEOService()
    }
    return SEOService.instance
  }

  public setSEO (config: SEOConfig): void {
    const baseUrl = 'https://estaterwanda.com'
    const fullUrl = config.url ? `${baseUrl}${config.url}` : baseUrl
    const imageUrl = config.image ? `${baseUrl}${config.image}` : `${baseUrl}/img/real-estate.png`

    useHead({
      title: config.title,
      meta: [
        {
          name: 'description',
          content: config.description
        },
        {
          name: 'keywords',
          content: config.keywords || 'real estate, properties, houses for sale, apartments for rent, Rwanda, Kigali'
        },
        {
          name: 'author',
          content: 'Estate Rwanda'
        },
        {
          name: 'robots',
          content: 'index, follow'
        },
        // Open Graph
        {
          property: 'og:title',
          content: config.title
        },
        {
          property: 'og:description',
          content: config.description
        },
        {
          property: 'og:image',
          content: imageUrl
        },
        {
          property: 'og:url',
          content: fullUrl
        },
        {
          property: 'og:type',
          content: config.type || 'website'
        },
        {
          property: 'og:site_name',
          content: 'Estate Rwanda'
        },
        // Twitter Card
        {
          name: 'twitter:card',
          content: 'summary_large_image'
        },
        {
          name: 'twitter:title',
          content: config.title
        },
        {
          name: 'twitter:description',
          content: config.description
        },
        {
          name: 'twitter:image',
          content: imageUrl
        }
      ],
      link: [
        // Canonical URL
        {
          rel: 'canonical',
          href: fullUrl
        }
      ],
      script: config.structuredData
        ? [
            {
              type: 'application/ld+json',
              children: JSON.stringify(config.structuredData)
            }
          ]
        : []
    })
  }

  public setPropertySEO (property: any): void {
    const structuredData: PropertyStructuredData = {
      '@context': 'https://schema.org',
      '@type': 'Product',
      name: property.title,
      description: property.description || `Property for ${property.status.toLowerCase()} in ${property.location}`,
      image: property.images || [property.image],
      address: {
        '@type': 'PostalAddress',
        addressLocality: property.location,
        addressCountry: 'RW'
      },
      offers: {
        '@type': 'Offer',
        price: property.price.replace(/[^\d]/g, ''),
        priceCurrency: 'RWF',
        availability: property.status === 'For Sale'
          ? 'https://schema.org/InStock'
          : 'https://schema.org/InStock'
      },
      numberOfRooms: property.beds,
      floorSize: {
        '@type': 'QuantitativeValue',
        value: property.sqft,
        unitCode: 'MTK'
      }
    }

    this.setSEO({
      title: `${property.title} - ${property.location} | Estate Rwanda`,
      description: `${property.title} for ${property.status.toLowerCase()} in ${property.location}. ${property.beds} beds, ${property.baths} baths, ${property.sqft} sqft. ${property.price}`,
      keywords: `property, ${property.type}, ${property.status.toLowerCase()}, ${property.location}, Rwanda`,
      image: property.images?.[0] || property.image,
      url: `/property-details?id=${property.id}`,
      type: 'property',
      structuredData
    })
  }

  public setBreadcrumbSEO (breadcrumbs: Array<{ name: string; url?: string }>): void {
    const structuredData: BreadcrumbStructuredData = {
      '@context': 'https://schema.org',
      '@type': 'BreadcrumbList',
      itemListElement: breadcrumbs.map((crumb, index) => ({
        '@type': 'ListItem',
        position: index + 1,
        name: crumb.name,
        item: crumb.url ? `https://estaterwanda.com${crumb.url}` : undefined
      }))
    }

    // Add breadcrumb structured data to existing head
    useHead({
      script: [
        {
          type: 'application/ld+json',
          children: JSON.stringify(structuredData)
        }
      ]
    })
  }

  public setHomeSEO (): void {
    this.setSEO({
      title: 'Estate Rwanda',
      description: 'Discover the best properties for sale and rent in Rwanda. Browse apartments, houses, commercial properties, and land for sale and rent across Rwanda. Your trusted partner for real estate in Rwanda.',
      keywords: 'real estate Rwanda, properties for sale Rwanda, houses for rent Rwanda, apartments Rwanda, land for sale Rwanda, commercial property Rwanda, buy property Rwanda, rent property Rwanda',
      type: 'website'
    })
  }

  public setPropertiesListSEO (filters?: any): void {
    const filterText = filters ? ` - ${Object.values(filters).filter(Boolean).join(', ')}` : ''
    this.setSEO({
      title: `Properties${filterText} | Estate Rwanda`,
      description: 'Browse our comprehensive collection of properties for sale and rent in Rwanda. Find your perfect home, apartment, or commercial property with detailed listings and professional support.',
      keywords: 'properties Rwanda, houses for sale, apartments for rent, commercial property, real estate listings',
      type: 'website'
    })
  }

  public setAboutSEO (): void {
    this.setSEO({
      title: 'About | Estate Rwanda',
      description: 'Learn about Estate Rwanda, your trusted partner in real estate across Rwanda. We connect buyers, sellers, and renters with verified properties and professional agents.',
      keywords: 'about Estate Rwanda, real estate company Rwanda, property agents Rwanda, trusted real estate',
      type: 'website'
    })
  }

  public setContactSEO (): void {
    this.setSEO({
      title: 'Contact | Estate Rwanda',
      description: 'Contact Estate Rwanda for all your real estate needs. Our professional team is here to help you find the perfect property or list your property for sale or rent.',
      keywords: 'contact Estate Rwanda, real estate contact, property consultation, real estate support',
      type: 'website'
    })
  }
}

export default SEOService.getInstance()
